import { call, put, takeLatest } from "redux-saga/effects";
import { ActionWithPayload, Filter, PaginatedResponse } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";
import { Form, Hospital, RolePermission } from "@shared/models";

import api from "../api";
import {
  getHospital,
  getHospitalFormsSettings,
  getHospitalPermissions,
  getHospitals,
  updateHospital,
  updateHospitalPermission,
} from "./actions";
import { UpdateHospitalPermissionPayload, UpdateHospitalPermissionSuccess, UpdateHospitalSuccess } from "../interfaces";

function* getHospitalsSaga({ payload }: ActionWithPayload<Filter>) {
  try {
    yield put(startLoading());
    const response: PaginatedResponse<Hospital> = yield call(api.getHospitals, payload);
    yield put(stopLoading());
    yield put(
      getHospitals.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getHospitals.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getHospitalFormsSettingsSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: { forms: Form[] } = yield call(api.getHospitalFormsSettings, String(payload.id));
    yield put(stopLoading());
    yield put(getHospitalFormsSettings.success(response.forms));
  } catch (error) {
    yield put(getHospitalFormsSettings.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getHospitalSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: Hospital = yield call(api.getHospital, String(payload.id));
    yield put(stopLoading());
    yield put(getHospital.success(response));
  } catch (error) {
    yield put(getHospital.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getHospitalPermissionsSaga({ payload }: ActionWithPayload<{ id: number }>) {
  try {
    yield put(startLoading());
    const response: RolePermission[] = yield call(api.getHospitalPermissions, String(payload.id));
    yield put(stopLoading());
    yield put(getHospitalPermissions.success(response));
  } catch (error) {
    yield put(getHospitalPermissions.failure(error as Error));
    yield put(stopLoading());
  }
}

function* updateHospitalPermissionSaga({ payload }: ActionWithPayload<UpdateHospitalPermissionPayload>) {
  try {
    yield put(startLoading());
    const response: UpdateHospitalPermissionSuccess = yield call(api.updateHospitalPermissions, payload);
    yield put(stopLoading());
    yield put(updateHospitalPermission.success({ ...response, ...payload }));
  } catch (error) {
    yield put(updateHospitalPermission.failure(error as Error));
    yield put(stopLoading());
  }
}

function* updateHospitalSaga({ payload }: ActionWithPayload<Partial<Hospital>>) {
  try {
    yield put(startLoading());
    const response: UpdateHospitalSuccess = yield call(api.updateHospital, payload);
    yield put(stopLoading());
    yield put(updateHospital.success(response));
  } catch (error) {
    yield put(updateHospital.failure(error as Error));
    yield put(stopLoading());
  }
}

function* hospitalSaga() {
  yield takeLatest(getHospitals.request, getHospitalsSaga);
  yield takeLatest(getHospital.request, getHospitalSaga);
  yield takeLatest(updateHospital.request, updateHospitalSaga);
  yield takeLatest(getHospitalPermissions.request, getHospitalPermissionsSaga);
  yield takeLatest(updateHospitalPermission.request, updateHospitalPermissionSaga);
  yield takeLatest(getHospitalFormsSettings.request, getHospitalFormsSettingsSaga);
}

export default hospitalSaga;
